/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, Inject, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { RedirectHelper } from '../../../page-modules/resource/store/editor/content/helpers/redirect.helper';
import { VersionHelper } from '../../../shared/helpers/version.helper';
import { AccessTokenService } from '../../services/access-token.service';
import { USER_IS_LOGGED_IN } from '../../services/api-basic-auth.service';
import { AUTH_SERVICE, BasicAuthService } from '../../services/basic-auth.service';
import { LtiAuthService } from '../../services/lti-auth.service';
import * as UserAuthActions from '../../store/user-auth.actions';
import { UserAuthState } from '../../store/user-auth.state';

const SETUP_SECTIONS: Record<string, string> = {
  overview: '',
  marking: 'marking',
  gradebook: 'gradebook',
  skills: 'skills',
  badges: 'badges',
};

@Component({
  template: '',
})
export class MoodleLaunchComponent implements OnInit {
  constructor(
    private store: Store,
    private activatedRoute: ActivatedRoute,
    @Inject(AUTH_SERVICE) private authService: BasicAuthService,
    private router: Router,
    private ngZone: NgZone,
    private readonly accessTokenService: AccessTokenService,
    private readonly ltiAuthService: LtiAuthService,
  ) {
    this.accessTokenService.removeAccessToken();
    this.ltiAuthService.removeLtiRequestId();
  }

  ngOnInit() {
    const requestId = this.activatedRoute.snapshot.queryParamMap.get('requestId');
    if (!requestId) {
      this.redirectTo('/lti/registration/error');
      return;
    }
    this.authService.getLtiToken(requestId, false).subscribe({
      next: ({ isSuccess, value }) => {
        if (!isSuccess) {
          this.redirectTo('/lti/registration/error');
          return;
        }
        this.accessTokenService.saveAccessToken(value.access_token);
        localStorage.setItem(USER_IS_LOGGED_IN, 'true');
        this.store
          .dispatch([
            new UserAuthActions.FetchUserDetails(),
            new UserAuthActions.RedirectToAuthenticatedAppSuccess(),
            new UserAuthActions.GetHomePageTitleAndUri(),
          ])
          .subscribe(() => this.redirectTo(this.destination(this.activatedRoute.snapshot.queryParamMap)));
      },
      error: () => this.redirectTo('/lti/registration/error'),
    });
  }

  private destination(parameters: ParamMap): string {
    const content = encodeURIComponent(parameters.get('content') ?? '');
    switch (parameters.get('target')) {
      case 'COURSE':
        return `/courses/${content}/content`;
      case 'PROGRAM':
        return `/programs/${content}/content`;
      case 'PLAYLIST':
        return `/playlists/${content}`;
      case 'ASSIGNMENT':
        return this.isStaff()
          ? `/lti/course/setup/${content}/marking/${encodeURIComponent(parameters.get('assignment') ?? '')}`
          : `/courses/${content}/assignments/${encodeURIComponent(parameters.get('assignmentUri') ?? '')}`;
      case 'SETUP': {
        const section = SETUP_SECTIONS[parameters.get('section')] ?? SETUP_SECTIONS.overview;
        return `/lti/course/setup/${content}${section ? `/${section}` : ''}`;
      }
      case 'PROFILE':
        return VersionHelper.isV3Enabled() ? '/profile' : '/folio';
      default:
        return this.store.selectSnapshot(UserAuthState.homePageUri);
    }
  }

  private isStaff(): boolean {
    return (
      this.store.selectSnapshot(UserAuthState.userHasInstructorRole) ||
      this.store.selectSnapshot(UserAuthState.userHasAdminRole) ||
      this.store.selectSnapshot(UserAuthState.userHasSuperAdminRole)
    );
  }

  private redirectTo(url: string) {
    RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, url);
  }
}
